import {EditorSelection, type Extension, RangeSetBuilder, StateEffect, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'

import {parseMarkdown} from '../../markdown/parse-markdown'
import type {CstNode, CstNodeType} from '../../markdown/syntax-tree/syntax-tree'
import {isListType, walkCst} from '../../markdown/syntax-tree/utils'
import {EmptySpanWidget} from '../widgets/empty-span'
import {ThematicBreakWidget} from '../widgets/thematic-break'
import styles from './decorate-markdown.module.css'

function overlapsNode(selectionFrom: number, selectionTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (selectionFrom >= nodeFrom && selectionFrom < nodeTo) ||
    (selectionTo > nodeFrom && selectionTo <= nodeTo) ||
    (selectionFrom < nodeFrom && selectionTo > nodeTo)
  )
}

/** Ancestor node types with data about whether they are active or not. For handling children of active nodes. */
type NodeContext = Array<{type: CstNodeType; active: boolean}>

function getNodeMark(node: CstNode, active: boolean, context: NodeContext): Decoration | undefined {
  const blockClass = clsx(styles.block, active && styles.activeBlock)

  // lazy to avoid unecessary calculations for nodes that don't need this
  const withinActiveList = () => context.some(parent => parent.active && isListType(parent.type))
  const withinActiveTable = () => context.some(parent => parent.type === 'table' && parent.active)

  switch (node.type) {
    case 'blockQuoteMarker':
    case 'strongSequence':
    case 'emphasisSequence':
    case 'codeTextSequence': // inline code delimiters
    case 'strikethroughSequence':
    case 'codeFencedFence':
    case 'codeTextPadding':
    case 'atxHeadingSequence': // ### heading indicator
    case 'setextHeadingLine': // === or --- heading underline
    case 'labelMarker': // brackets around link text
    case 'resource': // link (url) part
    case 'linePrefix': // indentation for indented code blocks
    case 'listItemIndent':
    case 'listItemPrefix':
    case 'tableCellDivider':
    case 'autolinkMarker':
      return Decoration.mark({class: styles.delimiter})
    case 'hardBreakEscape':
    case 'escapeMarker':
      return Decoration.mark({class: clsx(styles.delimiter, styles.escapeMarker)})
    case 'codeFencedFenceInfo':
      return Decoration.mark({class: styles.codeInfo})
    case 'strong':
      return Decoration.mark({tagName: 'strong'})
    case 'emphasis':
      return Decoration.mark({tagName: 'em'})
    case 'codeText':
      return Decoration.mark({class: styles.backgroundColorEditables, tagName: 'code'})
    case 'htmlText':
      return Decoration.mark({tagName: 'code', class: styles.html})
    case 'strikethrough':
      return Decoration.mark({tagName: 'del'})
    case 'link':
    case 'autolink':
    case 'literalAutolink':
      return Decoration.mark({
        tagName: 'a',
        attributes: {
          href: node.href ?? '#',
        },
      })
    case 'thematicBreak':
      if (active) {
        // When active, show the original text
        return Decoration.mark({tagName: 'span'})
      } else {
        // When not active, replace with horizontal rule widget
        return Decoration.replace({
          widget: new ThematicBreakWidget(active),
          block: false,
        })
      }
    case 'blockQuote':
      return Decoration.mark({tagName: 'blockquote', class: blockClass})
    case 'codeFenced':
    case 'codeIndented':
      return Decoration.mark({tagName: 'pre', class: clsx(blockClass, styles.backgroundColorEditables)})
    case 'htmlFlow':
      return Decoration.mark({tagName: 'pre', class: clsx(blockClass, styles.html)})
    case 'paragraph':
      return Decoration.mark({tagName: 'p', class: clsx(blockClass, styles.paragraph)})
    case 'atxHeading':
    case 'setextHeading':
      return Decoration.mark({tagName: `h${node.depth}`, class: blockClass})
    case 'listUnordered':
    case 'listOrdered': {
      if (withinActiveList()) return undefined
      return Decoration.mark({tagName: node.type === 'listOrdered' ? 'ol' : 'ul', class: blockClass})
    }
    case 'listItem': {
      if (withinActiveList()) return undefined
      return Decoration.mark({tagName: 'li'})
    }
    case 'table':
      // tables revert to plain text when active (for now, until we build a table editor)
      return active
        ? Decoration.mark({tagName: 'pre', class: blockClass})
        : Decoration.mark({
            tagName: 'table',
            class: clsx(blockClass, styles.table),
            // contenteditable must be disabled for the `white-space` styling to take effect, which is necessary to
            // collapse extra whitespace typically used to align table delimiters in the Markdown source, particularly
            // for right- and center-aligned columns
            attributes: {contenteditable: 'false'},
          })
    case 'tableHead':
      return withinActiveTable() ? undefined : Decoration.mark({tagName: 'thead'})
    case 'tableBody':
      return withinActiveTable() ? undefined : Decoration.mark({tagName: 'tbody'})
    case 'tableRow':
      return withinActiveTable() ? undefined : Decoration.mark({tagName: 'tr', class: styles.backgroundColorEditables})
    case 'tableHeader':
    case 'tableData':
      return withinActiveTable()
        ? undefined
        : Decoration.mark({
            tagName: node.type === 'tableHeader' ? 'th' : 'td',
            class: clsx({
              [styles.alignRight]: node.alignment === 'right',
              [styles.alignCenter]: node.alignment === 'center',
            }),
          })
    case 'tableDelimiter':
      return withinActiveTable() ? undefined : Decoration.mark({tagName: 'td'})
    case 'tableDelimiterRow':
      return withinActiveTable() ? undefined : Decoration.mark({tagName: 'tr', class: styles.delimiter})
    default:
      return undefined
  }
}

function buildDecorations(tree: CstNode, selection: EditorSelection | null): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()

  const context: NodeContext = []

  walkCst(tree, {
    enter(node) {
      const overlapsSelection =
        selection?.ranges.some(range => overlapsNode(range.from, range.to, node.start, node.end)) ?? false

      const mark = getNodeMark(node, overlapsSelection, context)
      if (mark) {
        // For decorations with the same 'from' and 'to' value, CodeMirror renders the first tag in the set and wraps
        // the second tag around it. However, our tree walker encounters parents first and then children, which causes
        // incorrect element order. For example, a list with one element would result in `<li><ol>...</ol></li>`
        // because the list and item have the same start and end offsets.
        //
        // To fix this, CodeMirror supports a secondary comparison by `startSide`. Lower `startSide` = outermost
        // element.
        mark.startSide = context.length
        builder.add(node.start, node.end, mark)
      }

      context.push({type: node.type, active: overlapsSelection})
    },
    exit() {
      context.pop()
    },
  })

  // Place an empty `span` element at the very end of the document, following any line breaks. The DOM will always
  // suppress a line break at the end of a block container, which would prevent the user from typing a newline at the
  // end of the doc. By placing an invisible span after the last character, we ensure that a trailing line break is
  // never the last thing in the document.
  builder.add(tree.end, tree.end, Decoration.widget({widget: EmptySpanWidget}))

  return builder.finish()
}

interface MarkdownDecorationsState {
  tree: CstNode
  focused: boolean
  decorations: DecorationSet
}

const focusChangeEffect = StateEffect.define<boolean>()

const focusChangeExtension = EditorView.focusChangeEffect.of((_, focused) => focusChangeEffect.of(focused))

const markdownDecorationsState = StateField.define<MarkdownDecorationsState>({
  create: s => {
    const doc = s.doc.toString()
    const tree = parseMarkdown(doc)
    return {tree, decorations: buildDecorations(tree, s.selection), focused: false}
  },
  update: (prev, tr) => {
    const focusChangeEvent = tr.effects.find(e => e.is(focusChangeEffect))

    const selectionChanged = !tr.startState.selection.eq(tr.newSelection)
    const focusChanged = focusChangeEvent !== undefined
    const docChanged = tr.docChanged

    if (!selectionChanged && !focusChanged && !docChanged) return prev

    const doc = tr.newDoc.toString()

    // avoid reparsing the tree if only the selection changes
    const tree = docChanged ? parseMarkdown(doc) : prev.tree

    const focused = focusChangeEvent?.value ?? prev.focused
    const selection = focused ? tr.newSelection : null

    return {
      tree,
      decorations: buildDecorations(tree, selection),
      focused,
    }
  },
  provide: field => EditorView.decorations.from(field, state => state.decorations),
})

export const decorateMarkdownPlugin: Extension = [focusChangeExtension, markdownDecorationsState]
